import { useState, Children, isValidElement, type ReactElement } from "react";
import cx from "classnames";
import { motion } from "motion/react";
import { ResultOf, readFragment } from "gql.tada";
import { CaseStudyBodySectionById } from "@queries/entries/CaseStudyBodySectionById";
import { CaseStudyBodySectionFragment } from "@queries/fragments/CaseStudyBodySectionFragment";
import { Button } from "@components/shared/core/Button";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, MARKS, Document as RichTextDocument } from "@contentful/rich-text-types";
import { useFootnoteContext } from "@utils/FootnoteContext";
import { useTranslation } from "@services/i18n";
import { OBELIX_CAROUSEL_KEYS } from "@utils/obelixKeys";

type SectionData = ResultOf<typeof CaseStudyBodySectionFragment>;
type ImageItem = NonNullable<
  NonNullable<SectionData["imageSlidesCollection"]>["items"][number]
>;

const ArrowIcon = ({ className = "" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="16"
    fill="none"
    aria-hidden
    className={className}
  >
    <path
      d="m8.5 1 7 7-7 7M14.833 8H1.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CaseStudyBodySection = ({
  entry: { caseStudyBodySection: entry },
  index = 0,
}: {
  entry: ResultOf<typeof CaseStudyBodySectionById>;
  index?: number;
}) => {
  const section = entry ? readFragment(CaseStudyBodySectionFragment, entry) : null;
  if (!section) return null;

  // Stripe the second/even section with the blue-deep background (per Figma).
  // Zero-indexed: second item -> index 1. Later this may come from the page
  // theme or a scroll-driven "in-focus" animation.
  const hasBackground = index % 2 === 1;

  const images: ImageItem[] = (section.imageSlidesCollection?.items ?? []).filter(
    (img): img is ImageItem => !!img?.url
  );

  return (
    <section
      className={cx(
        "w-full px-6 dsk:px-[32px] py-10 dsk:py-[60px]",
        hasBackground && "bg-palette-current-deep"
      )}
    >
      <div className="flex flex-col gap-6 dsk:flex-row dsk:gap-[74px] items-start">
        {section.title && (
          <h3 className="text-palette-current-highlight font-['Netflix_Sans_Title'] font-medium text-[32px] dsk:text-[40px] leading-[0.94] tracking-[-0.02em] shrink-0 dsk:w-[184px] capitalize">
            {section.title}
          </h3>
        )}
        <div className="w-full dsk:max-w-[680px] flex flex-col gap-10">
          {section.body && <CaseStudyBodyText body={section.body} />}
          {images.length > 0 && <CaseStudyBodyCarousel images={images} />}
        </div>
      </div>
    </section>
  );
};

const CaseStudyBodyText = ({ body }: { body: { json: unknown } }) => {
  const { getNumber } = useFootnoteContext();

  return (
    <div className="flex flex-col gap-5">
      {documentToReactComponents(body.json as RichTextDocument, {
        renderMark: {
          [MARKS.BOLD]: (text) => (
            <strong className="text-palette-current-highlight">{text}</strong>
          ),
        },
        renderNode: {
          [BLOCKS.PARAGRAPH]: (_, children) => (
            <p className="text-neutral-cream text-[18px] leading-[1.4] tracking-[-0.02em]">
              {children}
            </p>
          ),
          [BLOCKS.UL_LIST]: (_, children) => (
            <ul className="list-disc pl-5 flex flex-col gap-2 text-neutral-cream text-[18px] tracking-[-0.01em]">
              {children}
            </ul>
          ),
          [BLOCKS.LIST_ITEM]: (_, children) => (
            <li className="leading-[1.2]">
              {Children.map(children as ReactElement[], (child) =>
                isValidElement(child) && child.type === "p"
                  ? (child.props as { children: ReactElement }).children
                  : child
              )}
            </li>
          ),
          [INLINES.EMBEDDED_ENTRY]: (node) => {
            const id = node.data?.target?.sys?.id as string | undefined;
            if (!id) return null;
            const number = getNumber(id);
            if (number === null) return null;
            return <sup className="text-[0.7em] leading-none">{number}</sup>;
          },
        },
      })}
    </div>
  );
};

const CaseStudyBodyCarousel = ({
  images,
}: {
  images: ImageItem[];
}) => {
  const [index, setIndex] = useState(0);
  const total = images.length;
  const hasMultiple = total > 1;
  const { t } = useTranslation();
  const previousLabel = t(
    OBELIX_CAROUSEL_KEYS.previous.obelixKey,
    "Previous image"
  );
  const nextLabel = t(
    OBELIX_CAROUSEL_KEYS.next.obelixKey,
    "Next image"
  );

  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  return (
    // On desktop, the row extends 63px beyond the text column on each side
    // via negative horizontal margin, so the arrows sit outside the image
    // while the image (flex-1) stays aligned with the text column above.
    // On mobile, no negative margin -> arrows shrink the image from the sides.
    <div className="flex items-center gap-[21px] dsk:-mx-[63px]">
      {hasMultiple && (
        <Button
          variant="light-on-blur"
          size="inherit"
          className="shrink-0 size-[42px]"
          onClick={prev}
          aria-label={previousLabel}
        >
          <ArrowIcon className="rotate-180" />
        </Button>
      )}
      <div className="relative flex-1 aspect-video rounded-lg overflow-hidden">
        {images.map((img, i) => {
          const offset = i - index;

          if (img.contentType?.includes('video')) {
            return (
              <motion.video
                key={img.url}
                src={img.url ?? ''}
                controls
                className="absolute inset-0 size-full object-cover rounded-lg"
                animate={{ x: `calc(${offset * 100}% + ${offset * 24}px)` }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              />
            )
          }

          return (
            <motion.img
              key={img.url}
              src={img.url ?? ""}
              alt={img.title ?? ""}
              className="absolute inset-0 size-full object-cover rounded-lg"
              animate={{ x: `calc(${offset * 100}% + ${offset * 24}px)` }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            />
          );
        })}
      </div>
      {hasMultiple && (
        <Button
          variant="light-on-blur"
          size="inherit"
          className="shrink-0 size-[42px]"
          onClick={next}
          aria-label={nextLabel}
        >
          <ArrowIcon />
        </Button>
      )}
    </div>
  );
};
